import React, { useRef, useState, useEffect } from 'react';

// Add font-face declaration at the top
const fontFace = new FontFace('ds', `url(${process.env.PUBLIC_URL}/assets/fonts/ds.ttf)`);
document.fonts.add(fontFace);
fontFace.load().then(() => {
  console.log('Font loaded successfully');
}).catch(err => {
  console.error('Error loading font:', err);
});


// Global settings
const FISH_BASE_SIZE = 12; // Adjusted base size for fish (width and height)
const FONT_BASE_SIZE = 2; // Base font size for fish names
let FISH_THEME; // Set to 1 for ocean theme, 2 for bass theme
const ANIMATION_SPEED = 30; // Frames per second
const MOVEMENT_SPEED = 0.1; // Pixels per frame
const MAX_FISH = 15; // Maximum number of fish to display
const LATERALCHANGESPEED = 0.005;
const MEDIALCHANGESPEED = 0.002;
const SCALE_FACTOR = 1.5; // Scale factor to control fish size change

class Fish {
  constructor(x, y, speed, name, type, theme, canvasWidth, canvasHeight, fishSize) {
    this.image = new Image();
    this.imageR = new Image();
    this.imageL = new Image();
    this.x = x;
    this.y = y;
    this.width = fishSize; // Use dynamic fish size
    this.height = fishSize; // Use dynamic fish size
    this.speed = MOVEMENT_SPEED;
    this.checkH = false;
    this.checkV = false;
    this.numFrames = 0;
    this.frameWidth = 200;
    this.frameHeight = 200;
    this.spriteSheetX = 0;
    this.spriteSheetY = 0;
    this.currentFrame = 0;
    this.animationSpeed = ANIMATION_SPEED;
    this.lastFrameTime = 0;
    this.name = name;
    this.type = type;
    this.isAlive = true;
    this.theme = theme;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.spritesheetLoaded = false;
    this.currentImage = null;
  }

  loadSpritesheets() {
    // Helper function to load images
    const loadImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = src;
      });
    };

    // Set sprite sheet paths and frames based on type and theme
    let leftPath, rightPath, frames;
    switch (this.type) {
      case "1":
        if (this.theme === "Frutiger Aero") {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/angelL.png', process.env.PUBLIC_URL + '/assets/fish/angelR.png', 25];
        } else {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/Bass_spritesheet.png', process.env.PUBLIC_URL + '/assets/fish/Bass_spritesheetR.png', 60];
        }
        break;
      case "2":
        if (this.theme === "Frutiger Aero") {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/batL.png', process.env.PUBLIC_URL + '/assets/fish/batR.png', 26];
        } else {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/Beepo_Bass_spritesheetL.png', process.env.PUBLIC_URL + '/assets/fish/Beepo_Bass_spritesheetR.png', 60];
        }
        break;
      case "3":
        if (this.theme === "Frutiger Aero") {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/longnoseL.png', process.env.PUBLIC_URL + '/assets/fish/longnoseR.png', 26];
        } else {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/Sherbert_Bass_spritesheetL.png', process.env.PUBLIC_URL + '/assets/fish/Sherbert_Bass_spritesheetR.png', 60];
        }
        break;
      case "4":
        if (this.theme === "Frutiger Aero") {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/unicornL.png', process.env.PUBLIC_URL + '/assets/fish/unicornR.png', 26];
        } else {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/Uber_Bass_spritesheetL.png', process.env.PUBLIC_URL + '/assets/fish/Uber_Bass_spritesheetR.png', 60];
        }
        break;
      case "5":
        if (this.theme === "Frutiger Aero") {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/clownL.png', process.env.PUBLIC_URL + '/assets/fish/clownR.png', 26];
        } else {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/fish_spritesheet.png', process.env.PUBLIC_URL +  '/assets/fish/fish_spritesheetR.png', 45];
        }
        break;
      case "6":
        if (this.theme === "Frutiger Aero") {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/tangL.png', process.env.PUBLIC_URL + '/assets/fish/tangR.png', 26];
        } else {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/rainbow_Bass_spritesheetL.png', process.env.PUBLIC_URL + '/assets/fish/rainbow_Bass_spritesheetR.png', 60];
        }
        break;
      default:
        if (this.theme === "Frutiger Aero") {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/angelL.png', process.env.PUBLIC_URL + '/assets/fish/angelR.png', 25];
        } else {
          [leftPath, rightPath, frames] = [process.env.PUBLIC_URL + '/assets/fish/Bass_spritesheet.png', process.env.PUBLIC_URL + '/assets/fish/Bass_spritesheetR.png', 60];
        }
    }

    // Load both images
    return Promise.all([
      loadImage(leftPath),
      loadImage(rightPath)
    ]).then(([leftImg, rightImg]) => {
      this.imageL = leftImg;
      this.imageR = rightImg;
      this.numFrames = frames;
      this.spritesheetLoaded = true;
      this.currentImage = this.imageR; // Initial direction
    });
  }

  animate(ctx, deltaTime) {
    if (!this.spritesheetLoaded || !this.isAlive) return;

    // Update animation frame
    this.lastFrameTime += deltaTime;
    if (this.lastFrameTime >= 1000 / this.animationSpeed) {
      this.currentFrame = (this.currentFrame + 1) % this.numFrames;
      this.lastFrameTime = 0;
    }

    // Draw the current frame
    const sourceX = this.spriteSheetX + this.currentFrame * this.frameWidth;
    const sourceY = this.spriteSheetY;

    ctx.drawImage(
      this.currentImage,
      sourceX,
      sourceY,
      this.frameWidth,
      this.frameHeight,
      this.x,
      this.y,
      this.width,
      this.height
    );

    // Set the font to 'ds'
    const dynamicFontSize = Math.max(10, (this.canvasWidth / 200) * FONT_BASE_SIZE); // Minimum font size of 10px
    ctx.font = `${dynamicFontSize}px ds`; // Use 'ds' font
    ctx.fillStyle = "white";
    ctx.save();
    ctx.shadowColor = "black";
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Draw name directly above the fish
    const textWidth = ctx.measureText(this.name).width;
    const textX = this.x + (this.width / 2) + (textWidth / 2); // Center the text above the fish
    const textY = this.y - 5; // Adjust the vertical position slightly above the fish

    ctx.fillText(this.name, textX, textY); // Draw the name
    ctx.restore();
  }

  move() {
    if (!this.isAlive) return;

    // Update swimming bounds based on current canvas dimensions
    const leftBound = 10;
    const rightBound = this.canvasWidth - this.width - 10;
    const topBound = this.canvasHeight * .15;
    const bottomBound = this.canvasHeight - this.canvasHeight * .15;

    if (this.x <= leftBound) {
      this.checkH = false;
    } else if (this.y >= bottomBound) {
      this.checkV = false;
    } else if (this.x + this.width >= rightBound) {
      this.checkH = true;
    } else if (this.y <= topBound) {
      this.checkV = true;
    }

    if (Math.random() < LATERALCHANGESPEED) this.checkV = !this.checkV;
    if (Math.random() < MEDIALCHANGESPEED) this.checkH = !this.checkH;

    // Update current image based on direction
    if (this.checkH) {
      this.x -= this.speed;
      this.currentImage = this.imageL;
    } else {
      this.x += this.speed;
      this.currentImage = this.imageR;
    }

    if (this.checkV) {
      this.y += this.speed;
    } else {
      this.y -= this.speed;
    }
  }
}

const calculateFishSize = (canvasWidth) => {
  const baseSize = Math.min(canvasWidth, window.innerHeight) / 200; // Scale based on both width and height
  return baseSize * FISH_BASE_SIZE * SCALE_FACTOR; // Adjust fish size based on canvas width and scale factor
};

const FishTank = ({ commenters, theme, canvasbackground }) => {
  const canvasRef = useRef(null);
  const [fish, setFish] = useState([]);
  const [canvasSize, setCanvasSize] = useState({
    width: window.innerWidth * 0.66, // Changed to roughly 2/3 of window width
    height: window.innerHeight * 0.8,
  });

  useEffect(() => {
    FISH_THEME = theme;
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !commenters.length) return;
  
    let allFish = [];
    let x = 0;
  
    const initializeFish = async () => {
      const numFish = Math.min(commenters.length, MAX_FISH);
      const fishSize = calculateFishSize(canvasSize.width);
      for (let i = 0; i < numFish; i++) {
        let type = (x % 6 + 1).toString();
        const newFish = new Fish(
          Math.random() * canvas.width,
          Math.random() * (canvas.height - canvas.height * 0.2) + (canvas.height * 0.1),
          MOVEMENT_SPEED,
          commenters[i],
          type,
          FISH_THEME,
          canvas.width,
          canvas.height,
          fishSize
        );
        await newFish.loadSpritesheets();
        allFish.push(newFish);
        x++;
      }
      setFish(allFish);
    };
  
    initializeFish();
  }, [commenters, canvasSize, theme]);
  

  useEffect(() => {
    let animationFrameId;
    let lastTime = 0;

    const animate = (timestamp) => {
      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;

      const canvas = canvasRef.current;
      if (!canvas) return; // Add this check

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      fish.forEach((fishInstance) => {
        fishInstance.animate(ctx, deltaTime);
        fishInstance.move();
      });

      // Draw the most recent commenter in the bottom right corner
      ctx.font = '24px ds';
      ctx.fillStyle = 'white';
      ctx.textAlign = 'right';
      ctx.fillText("Most recent Commenter: " + (commenters[0] || 'NO COMMENTS ON MOST RECENT VIDEO YET'), canvas.width - 10, canvas.height - (canvas.height * 0.12));

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    // Cleanup function
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [fish, commenters]);

  const fishRef = useRef(fish);

useEffect(() => {
  fishRef.current = fish; // Keep ref updated
}, [fish]);

useEffect(() => {
  const resizeCanvas = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    setCanvasSize({ width, height });

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      const ratio = window.devicePixelRatio || 1;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.scale(ratio, ratio);

      const fishSize = calculateFishSize(width);
      fishRef.current.forEach(fishInstance => {
        fishInstance.width = fishSize;
        fishInstance.height = fishSize;
      });
    }
  };

  resizeCanvas();
  window.addEventListener("resize", resizeCanvas);

  return () => window.removeEventListener("resize", resizeCanvas);
}, []); 

  

  
  

  return (
    <div className="canvas-container" >
        <canvas 
            className='maincontentofpanel ytfish'
            ref={canvasRef} 
            width={canvasSize.width} 
            height={canvasSize.height}
            style={{ backgroundImage: `url(${canvasbackground})`}}
        />
    </div>
  );
};

export default FishTank;
